import {
    AddressApi, AddressCreateDto,
    AddressDto,
    ApplicationUserApi,
    ApplicationUserDto,
    ApplicationUserRole,
    OrderCreateDto,
    OrderUpdateDto, UnregisteredUserDataDto
} from "../../../api";
import {CrudFormItemProps, CrudFormModal, nameof} from "digimuth-components/dist";
import React, {useCallback, useEffect, useState} from "react";
import {Checkbox, Select, Switch} from "antd";
import {useSelector} from "react-redux";
import {getCurrentUser} from "../../../redux/applicationUserSlice";
import {useInjection} from "inversify-react";
import ApiService from "../../../services/apiService";
import {useForm} from "antd/es/form/Form";
import OrderOptionalMessage from "../../../components/orderOptionalMessage/optionalMessage";
import styles from "./orderModalForm.module.scss";
import PostalCode from "../../../components/formComponents/postalCode/postalCode";
import BuildingFlatNumber, {
    buildingAndFlat
} from "../../../components/formComponents/buildingFlatNumber/buildingFlatNumber";
import FirstAndLastName from "../../../components/formComponents/firstAndLastName/firstAndLastName";

const layout = {
    labelCol: {span: 7},
    wrapperCol: {span: 24},
    layout: "vertical",
};

const {Option} = Select;

export default function OrderModalForm(props: OrderModalProps) {
    const currentUser = useSelector(getCurrentUser);
    const apiService = useInjection(ApiService);
    const [form] = useForm();

    const [deliverSameAsBilling, setDeliverSameAsBilling] = useState<boolean>(true);
    const [addresses, setAddresses] = useState<AddressDto[]>();
    const [clients, setClients] = useState<ApplicationUserDto[]>([currentUser!]);
    const [orderForGuest, setOrderForGuest] = useState<boolean>(false);

    useEffect(() => {
        setOrderForGuest(!!props.initialValues && !props.initialValues?.clientId);
        setDeliverSameAsBilling(props.initialValues?.deliveryAddressSameAsBillingAddress ?? true);
    }, [props.initialValues])

    const fetchAddresses = useCallback(async (clientId: number) => {
        try {
            const response = await apiService.getApi(AddressApi)
                .apiAddressUserAddressesUserIdGet(clientId);
            setAddresses(response.data);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService]);

    const fetchClients = useCallback(async () => {
        try {
            const response = await apiService.getApi(ApplicationUserApi)
                .apiApplicationUserGet();
            setClients(response.data);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService]);

    const guestAddressItems = (addressTypeName: string, address?: AddressDto, postalCode?: string | null) => [
        {
            name: [addressTypeName, nameof<AddressCreateDto>("street")],
            label: "Ulica",
            rules: [
                {required: true, message: "To pole jest wymagane"}
            ]
        },
        {
            name: [addressTypeName, "buildingAndFlatNumber"],
            label: "Numer budynku/mieszkania",
            children: <BuildingFlatNumber initialValues={{
                building: address?.buildingNumber ?? "",
                flat: address?.flatNumber ?? ""
            }}/>,
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {
                    validator: (_: any, value: buildingAndFlat | null) => {
                        if (!value || (!value.building && !value.flat))
                            return Promise.reject("To pole jest wymagane");
                        if (!value.building && value.flat)
                            return Promise.reject("Number budynku jest wymagany");

                        return Promise.resolve();
                    }
                }
            ],
        },
        {
            name: [addressTypeName, nameof<AddressCreateDto>("postalCode")],
            label: "Kod pocztowy",
            rules: [
                {required: true, message: 'To pole jest wymagane'},
                {pattern: new RegExp('^[0-9]{2}-[0-9]{3}$'), message: 'To pole jest wymagane w formacie XX-XXX'}
            ],
            children: <PostalCode initialValues={postalCode}/>,
        },
        {
            name: [addressTypeName, nameof<AddressCreateDto>("city")],
            label: "Miasto",
            rules: [
                {required: true, message: 'To pole jest wymagane'},
            ],
        },
        {
            name: [addressTypeName, nameof<AddressCreateDto>("vatId")],
            label: "NIP",
            rules: [
                {
                    pattern: new RegExp('^[0-9]{10}$|^$'),
                    message: 'To pole jest wymagane w formacie XXXXXXXXXX'
                }
            ],
        },
        {
            name: [addressTypeName, nameof<AddressCreateDto>("companyName")],
            label: "Firma",
        },
    ];

    const guestFormItems: CrudFormItemProps[] = [
        {
            name: nameof<OrderCreateDto>("unregisteredUserData"),
            label: "Imię i nazwisko",
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {
                    validator: (_, value) => {
                        if (!value || value.firstName && value.lastName) return Promise.resolve();
                        if (value.firstName && !value.lastName) {
                            return Promise.reject(new Error("Nazwisko jest wymagane"));
                        }
                        if (!value.firstName && value.lastName) {
                            return Promise.reject(new Error("Imię jest wymagane"));
                        }
                    }
                }
            ],
            children: <FirstAndLastName/>
        },
        {
            name: nameof<OrderCreateDto>("email"),
            label: "Email",
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {type: "email", message: "Podano niepoprawny format adresu email"}
            ]
        },
        {
            name: nameof<OrderCreateDto>("phoneNumber"),
            label: "Numer telefonu",
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {pattern: new RegExp('^[0-9]{9}$'), message: "Numer telefonu jest wymagany w formie XXXXXXXXX"},
            ]
        },
        ...guestAddressItems(nameof<OrderCreateDto>("deliveryAddress"), props.initialValues?.deliveryAddress,
            props.initialValues?.deliveryAddress?.postalCode ?? null),
        {
            name: nameof<OrderCreateDto>("deliveryAddressSameAsBillingAddress"),
            label: "Poproszę fakturę",
            children: <Checkbox
                checked={!deliverSameAsBilling}
                onChange={(e) => setDeliverSameAsBilling(!e.target.checked)}
            />
        },
        ...!deliverSameAsBilling
            ? [...guestAddressItems(nameof<OrderCreateDto>("billingAddress"), props.initialValues?.billingAddress,
                props.initialValues?.billingAddress?.postalCode ?? null)]
            : [],
        {
            name: nameof<OrderCreateDto>("optionalMessage"),
            label: "Wiadomość do zamówienia",
            rules: [{
                validator: (_, value) => !value || value.length <= 250
                    ? Promise.resolve()
                    : Promise.reject(new Error("Długość wiadomości nie może przekraczać 250 znaków"))
            }],
            children: <OrderOptionalMessage/>
        }
    ]

    const formItems: CrudFormItemProps[] = [
        {
            name: nameof<OrderCreateDto>("clientId"),
            label: "Klient",
            rules: [
                {required: true, message: "To pole jest wymagane"}
            ],
            children: <Select onChange={async (value: number) => {
                const client = clients.find(x => x.id === value);
                form.setFieldsValue({
                    "email": client?.email,
                    "phoneNumber": client?.phoneNumber
                });
                await fetchAddresses(value);
            }}>
                {clients.map((client, index) => client && <Option
                        key={index} value={client.id}
                    >
                        {`${client.firstName} ${client.lastName}`}
                    </Option>
                )}
            </Select>
        },
        {
            name: nameof<OrderCreateDto>("email"),
            label: "Email",
            rules: [
                {required: true, message: 'To pole jest wymagane'},
                {type: "email", message: "Format adresu email jest niepoprawny"}
            ],
        },
        {
            name: nameof<OrderCreateDto>("phoneNumber"),
            label: "Numer telefonu",
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {pattern: new RegExp('^[0-9]{9}$'), message: "Numer telefonu jest wymagany w formie XXXXXXXXX"},
            ]
        },
        {
            name: nameof<OrderCreateDto>("deliveryAddressId"),
            label: "Adres dostawy",
            rules: [
                {required: true, message: "To pole jest wymagane"}
            ],
            children: <Select>
                {addresses?.map((address, index) => <Option
                    key={index} value={address.id}
                >
                    <span>{address.street} {address.buildingNumber}</span>{address.flatNumber &&
                    <span>/{address.flatNumber}</span>}
                    <span>, {address.postalCode} {address.city}</span>
                    {address.companyName && <span>, {address.companyName}</span>}
                    {address.vatId && <span>, NIP: {address.vatId}</span>}
                </Option>)}
            </Select>
        },
        {
            name: nameof<OrderCreateDto>("deliveryAddressSameAsBillingAddress"),
            label: "Poproszę fakturę",
            children: <Checkbox
                checked={!deliverSameAsBilling}
                onChange={(e) => setDeliverSameAsBilling(!e.target.checked)}
            />
        },
        ...!deliverSameAsBilling ? [
            {
                name: nameof<OrderCreateDto>("billingAddressId"),
                label: "Adres rozliczeniowy",
                rules: [
                    {required: true, message: "To pole jest wymagane"}
                ],
                children: <Select>
                    {addresses?.map((address, index) => <Option
                        key={index}
                        value={address.id}
                    >
                        <span>{address.street} {address.buildingNumber}</span>{address.flatNumber &&
                        <span>/{address.flatNumber}</span>}
                        <span>, {address.postalCode} {address.city}</span>
                        {address.companyName && <span>, {address.companyName}</span>}
                        {address.vatId && <span>, NIP: {address.vatId}</span>}
                    </Option>)}
                </Select>
            }
        ] : [],
        {
            name: nameof<OrderCreateDto>("optionalMessage"),
            label: "Wiadomość do zamówienia",
            rules: [{
                validator: (_, value) => !value || value.length <= 250
                    ? Promise.resolve()
                    : Promise.reject(new Error("Długość wiadomości nie może przekraczać 250 znaków"))
            }],
            children: <OrderOptionalMessage/>
        }
    ];

    const onSubmit = async (values: any) => {
        if (orderForGuest) {
            values.deliveryAddress.buildingNumber = values.deliveryAddress?.buildingAndFlatNumber?.building
                ?? props.initialValues?.deliveryAddress?.buildingNumber;
            values.deliveryAddress.flatNumber = values.deliveryAddress?.buildingAndFlatNumber?.flat
                ?? props.initialValues?.deliveryAddress?.buildingNumber;

            if (!deliverSameAsBilling) {
                values.billingAddress.buildingNumber = values.billingAddress?.buildingAndFlatNumber?.building
                    ?? props.initialValues?.billingAddress?.buildingNumber;
                values.billingAddress.flatNumber = values.billingAddress?.buildingAndFlatNumber?.flat
                    ?? props.initialValues?.billingAddress?.flatNumber;
            }
        }

        values.deliveryAddressSameAsBillingAddress = deliverSameAsBilling;

        await props.onSubmit(values);
    }

    useEffect(() => {
        if (currentUser?.roles?.includes(ApplicationUserRole.Admin)
            || currentUser?.roles?.includes(ApplicationUserRole.OrderManager)) {
            fetchClients();
        }
    }, [fetchClients, currentUser]);

    return <CrudFormModal
        form={form}
        forceRender={true}
        destroyOnClose={true}
        formItems={orderForGuest ? guestFormItems : formItems}
        onSubmit={onSubmit}
        onModalCancelClicked={() => props.setModalVisibility(false)}
        initialValues={props.initialValues}
        modalOkText={props.saveButtonTitle}
        modalTitle={props.modalTitle}
        isModalVisible={props.isModalVisible}
        modalCancelText="Anuluj"
        formProps={layout}
        width={800}
        preFormSection={<div className={styles.guestOrderContainer}>
            <strong>Zamówienie dla gościa:</strong>
            <Switch value={orderForGuest} onChange={setOrderForGuest} disabled={!!props.initialValues}/>
        </div>}
    />;
}

interface OrderModalProps {
    initialValues?: OrderCreateDto | OrderUpdateDto,
    isModalVisible: boolean;
    setModalVisibility: (isVisible: boolean) => any;
    onSubmit: (value: OrderCreateDto | OrderUpdateDto) => Promise<any>,
    modalTitle: string,
    saveButtonTitle: string,
    cancelButtonTitle: string;
}